import { removeUndefinedValues } from "../utils/firestore";
import { CollectionPaths } from "@order-up/common";
import {
  AppliedTax,
  Cart,
  IOrderData,
  ILocation,
  ITaxRate,
  Order,
  OrderItem,
  OrderModifier,
  OrderModifierGroup,
  TaxType,
  isCheckoutHeldOrderState,
} from "@order-up/models";
import { OrderDataSchema } from "@order-up/schemas";
import { validateAndCalculateOrderTime } from "../components/ordering/schedulingUtils";
import {
  DocumentWithId,
  FirestoreRepository,
} from "../interfaces/FirestoreRepository";
import { resolveDocIds } from "./resolveDocIds";
import { createLogger } from "../utils/logger";
import { counterService } from "./counters";
import { ordersApi } from "./ordersApi";

const logger = createLogger("OrderService");

const getCustomerOrders = async (
  customerId: string,
  tenantDocId: string,
  locationDocId: string,
  limit: number = 20,
): Promise<Order[]> => {
  logger.debug(`Getting orders for customer: ${customerId}`);

  try {
    const collectionPath = CollectionPaths.orders(tenantDocId, locationDocId);
    const orderRepo = new FirestoreRepository<Order>(collectionPath, Order);
    const orders = await orderRepo.find([
      { field: "customerId", operator: "==", value: customerId },
    ]);

    // Sort by creation date (most recent first) and apply limit. A
    // checkout-held order (online, not yet paid — possibly never) is not part
    // of anyone's order history.
    const sortedOrders = orders
      .filter((o) => !isCheckoutHeldOrderState(o.orderState))
      .sort((a, b) => {
        const aTime = a.createdAt?.getTime?.() || 0;
        const bTime = b.createdAt?.getTime?.() || 0;
        return bTime - aTime;
      })
      .slice(0, limit);

    logger.debug(`✅ Found ${sortedOrders.length} orders for customer`);
    return sortedOrders;
  } catch (error) {
    logger.error(`Failed to get customer orders`, error);
    throw error;
  }
};

/**
 * Gets an order by ID
 * @param orderId The order ID
 * @param tenantDocId The tenant Firestore document ID
 * @param locationDocId The restaurant Firestore document ID
 * @returns Promise resolving to the order or null if not found
 */
const getOrderById = async (
  orderId: number,
  tenantDocId: string,
  locationDocId: string,
): Promise<any | null> => {
  logger.debug(`Getting order by ID: ${orderId}`);

  try {
    const collectionPath = CollectionPaths.orders(tenantDocId, locationDocId);
    const orderRepo = new FirestoreRepository<Order>(collectionPath, Order);
    const orders = await orderRepo.find([
      { field: "orderId", operator: "==", value: orderId },
    ]);

    if (orders.length === 0) {
      logger.debug(`Order not found: ${orderId}`);
      return null;
    }

    logger.debug(`✅ Found order: ${orderId}`);
    return orders[0];
  } catch (error) {
    logger.error(`Failed to get order by ID`, error);
    throw error;
  }
};

/**
 * Gets an order by GUID (for QR code checkout of existing orders).
 *
 * SOC 2 (C9): this previously ran a Firestore `where('guid', ...)` LIST query,
 * which depended on a blanket `allow list` rule that let any authenticated
 * (incl. anonymous) session enumerate every restaurant's orders. It now
 * resolves the single record through the authenticated API (admin SDK,
 * server-side), with the unguessable GUID as the capability. Scope is now
 * numeric tenant/restaurant business IDs (the API resolves doc IDs itself).
 *
 * @param orderGuid The order GUID
 * @param tenantId The tenant numeric business ID
 * @param locationNumericId The restaurant numeric business ID
 * @returns Promise resolving to the order or null if not found
 */
const getOrderByGuid = async (
  orderGuid: string,
  tenantId: number,
  locationNumericId: number,
  accessToken?: string,
  // Returns the full order document the by-GUID API serializes (admin SDK reads
  // the whole doc). Typed `any` to preserve the pre-#177 contract: the checkout
  // confirmation page reads legacy display fields (tax/tip/orderType/
  // deliveryAddress) that the trimmed `Order` model no longer lists but the
  // stored document still carries. TODO: type a CheckoutOrderView + migrate the
  // page to the post-refactor model (paymentSummary, deliveryAddress-as-string).
): Promise<any | null> => {
  logger.debug(`Getting order by GUID: ${orderGuid}`);

  try {
    const order = await ordersApi.getOrderByGuid(
      orderGuid,
      tenantId,
      locationNumericId,
      accessToken,
    );
    if (!order) {
      logger.debug(`Order not found with GUID: ${orderGuid}`);
      return null;
    }
    logger.debug(`✅ Found order by GUID: ${orderGuid}`, {
      orderId: order.orderId,
      customerId: order.customerId,
      orderState: order.orderState,
    });
    return order;
  } catch (error) {
    logger.error(`Failed to get order by GUID`, error);
    throw error;
  }
};

/**
 * Gets an order by cart ID (prevents duplicate orders on payment retry).
 *
 * SOC 2 (C9): replaces the prior Firestore `where('cartId', ...)` LIST query
 * with the authenticated single-record API (see getOrderByGuid).
 *
 * @param cartId The cart ID (numeric)
 * @param tenantId The tenant numeric business ID
 * @param locationNumericId The restaurant numeric business ID
 * @returns Promise resolving to the order or null if not found
 */
const getOrderByCartId = async (
  cartId: number,
  tenantId: number,
  locationNumericId: number,
  // The `Order` model, exactly what `ordersApi.getOrderByCartId` parses — never
  // `any`: CheckoutPage once read a `paymentStatus` this model does not carry,
  // and only the `any` let that compile (it never fired — see
  // apps/checkout/src/lib/placedCartOrder.ts).
): Promise<Order | null> => {
  logger.debug(`Getting order by cart ID: ${cartId}`);

  try {
    const order = await ordersApi.getOrderByCartId(
      cartId,
      tenantId,
      locationNumericId,
    );
    if (!order) {
      logger.debug(`No order found for cart ID: ${cartId}`);
      return null;
    }
    logger.debug(`✅ Found order for cart ID ${cartId}`, {
      orderId: order.orderId,
      orderState: order.orderState,
    });
    return order;
  } catch (error) {
    logger.error(`Failed to get order by cart ID`, error);
    throw error;
  }
};

/**
 * Updates order status
 * @param orderId The order ID
 * @param status The new status
 * @param tenantDocId The tenant Firestore document ID
 * @param locationDocId The restaurant Firestore document ID
 * @param additionalData Optional additional data to update
 * @returns Promise resolving when update is complete
 */
const updateOrderStatus = async (
  orderId: number,
  orderState: string,
  tenantDocId: string,
  locationDocId: string,
  additionalData: any = {},
): Promise<void> => {
  logger.debug(`Updating order status: ${orderId} -> ${orderState}`);

  try {
    const updateData = {
      orderState,
      ...additionalData,
      updatedAt: FirestoreRepository.getServerTimestamp(),
    };

    const collectionPath = CollectionPaths.orders(tenantDocId, locationDocId);
    const orderRepo = new FirestoreRepository<Order>(collectionPath, Order);
    await orderRepo.updateByQuery(
      [{ field: "orderId", operator: "==", value: orderId }],
      updateData,
    );

    logger.debug(`✅ Successfully updated order status: ${orderId}`);
  } catch (error) {
    logger.error(`Failed to update order status`, error);
    throw error;
  }
};

/**
 * Gets order analytics for a restaurant
 * @param locationNumericId The restaurant ID
 * @param tenantId The tenant ID
 * @param tenantDocId The tenant Firestore document ID
 * @param locationDocId The restaurant Firestore document ID
 * @param startDate Start date for analytics
 * @param endDate End date for analytics
 * @returns Promise resolving to order analytics
 */
const getOrderAnalytics = async (
  locationNumericId: number,
  tenantId: number,
  tenantDocId: string,
  locationDocId: string,
  startDate: Date,
  endDate: Date,
) => {
  logger.debug(`Getting order analytics for restaurant: ${locationNumericId}`);

  try {
    const collectionPath = CollectionPaths.orders(tenantDocId, locationDocId);
    const orderRepo = new FirestoreRepository<Order>(collectionPath, Order);
    const orders = await orderRepo.find([
      { field: "restaurantId", operator: "==", value: locationNumericId },
      { field: "tenantId", operator: "==", value: tenantId },
      { field: "createdAt", operator: ">=", value: startDate },
      { field: "createdAt", operator: "<=", value: endDate },
    ]).then((rows) => rows.filter((o) => !isCheckoutHeldOrderState(o.orderState)));

    const analytics = {
      totalOrders: orders.length,
      totalRevenue: 0,
      averageOrderValue: 0,
      pickupOrders: 0,
      deliveryOrders: 0,
      completedOrders: 0,
      cancelledOrders: 0,
      ordersByStatus: {} as Record<string, number>,
    };

    orders.forEach((order) => {
      analytics.totalRevenue += order.total || 0;

      if (order.type === "takeOut") {
        analytics.pickupOrders++;
      } else if (order.type === "delivery") {
        analytics.deliveryOrders++;
      }

      if (order.orderState === "closed") {
        analytics.completedOrders++;
      } else if (order.orderState === "cancelled" || order.orderState === "voided") {
        analytics.cancelledOrders++;
      }

      // Count by orderState
      const state = order.orderState || "unknown";
      analytics.ordersByStatus[state] =
        (analytics.ordersByStatus[state] || 0) + 1;
    });

    analytics.averageOrderValue =
      analytics.totalOrders > 0
        ? analytics.totalRevenue / analytics.totalOrders
        : 0;

    logger.debug("✅ Successfully calculated order analytics");
    return analytics;
  } catch (error) {
    logger.error("Failed to get order analytics", error);
    throw error;
  }
};

/**
 * A bounded page of restaurant orders, plus whether the bound actually bit.
 *
 * `hasMore` exists because a capped read that does not say it was capped is
 * indistinguishable from a complete one — the Orders page renders under the
 * heading "All orders from every source", which silently became a lie at order
 * 101. Callers that present themselves as complete MUST surface this.
 */
export interface LocationOrdersPage {
  orders: Order[];
  /** True when more orders exist beyond `orders` for the same filters. */
  hasMore: boolean;
}

/**
 * Gets a bounded page of restaurant orders, reporting truncation.
 *
 * Fetches `limit + 1` and trims: `orders.length === limit` is ambiguous on its
 * own (a restaurant with exactly `limit` orders is not truncated), and guessing
 * wrong in either direction is worse than not reporting at all.
 *
 * NOTE on ordering: this sorts by `createdAt desc`, and Firestore's `orderBy`
 * omits documents that lack the sort field entirely while sorting null-valued
 * ones to the far end — where this limit then cuts them. `Order.createdAt` is
 * optional on the model, so that is a real exposure, not a hypothetical; see
 * the matching note in `checks.ts`, which drops its `orderBy` for exactly this
 * reason because POS devices write those documents directly. Orders are safer
 * only because every create path stamps `createdAt` today (POS
 * `OrderRepository.createOrder`, client `createOrder`, server `orderService`) —
 * an assumption verified by inspection and against production, NOT pinned by a
 * test. If it ever stops holding, drop the `orderBy` here, as `checks.ts` does,
 * rather than losing orders off the bottom of the sort.
 */
const getLocationOrdersPage = async (
  tenantId: number,
  locationNumericId: number,
  filters?: {
    orderSource?: "pos" | "online" | "thirdParty" | "kiosk" | "phone" | "api";
    externalSource?: "ubereats" | "doordash" | "grubhub" | "square" | "toast";
    orderState?:
      | "draft"
      | "pendingAcceptance"
      | "scheduled"
      | "open"
      | "held"
      | "closed"
      | "cancelled"
      | "voided";
    startDate?: Date;
    endDate?: Date;
  },
  limit: number = 100,
): Promise<LocationOrdersPage> => {
  logger.debug("Getting restaurant orders", {
    tenantId,
    restaurantId: locationNumericId,
    filters,
    limit,
  });

  try {
    // Resolve tenant/restaurant docIds via the shared session-cached resolver
    // (coalesces the repeated resolution the orders page pays across its 4
    // parallel source queries).
    const { tenantDocId, restaurantDocId: locationDocId } = await resolveDocIds(tenantId, locationNumericId);
    const collectionPath = CollectionPaths.orders(tenantDocId, locationDocId);
    const orderRepo = new FirestoreRepository<Order>(collectionPath, Order);

    // Build query conditions
    const conditions: Array<{ field: string; operator: any; value: any }> = [];

    if (filters?.orderSource) {
      conditions.push({
        field: "orderSource",
        operator: "==",
        value: filters.orderSource,
      });
    }

    if (filters?.externalSource) {
      conditions.push({
        field: "externalSource",
        operator: "==",
        value: filters.externalSource,
      });
    }

    if (filters?.orderState) {
      conditions.push({
        field: "orderState",
        operator: "==",
        value: filters.orderState,
      });
    }

    if (filters?.startDate) {
      conditions.push({
        field: "createdAt",
        operator: ">=",
        value: filters.startDate,
      });
    }

    if (filters?.endDate) {
      conditions.push({
        field: "createdAt",
        operator: "<=",
        value: filters.endDate,
      });
    }

    // Bound the read server-side: newest-first + limit pushed into Firestore, so
    // we transfer at most `limit` docs instead of downloading every matching
    // order and slicing client-side. Every filter shape used here has a
    // (<filter>, createdAt DESC) composite index (orderSource / externalSource /
    // orderSource+externalSource / orderState); the unfiltered case orders by the
    // single-field createdAt index.
    // Over-fetch by one so truncation is observed, not inferred.
    const fetched = await orderRepo.find(
      conditions,
      [{ field: "createdAt", direction: "desc" }],
      limit + 1,
    );

    const hasMore = fetched.length > limit;
    // A checkout-held order (online, persisted before its charge) is not an
    // order on any staff list. Filtered AFTER the limit so `hasMore` still
    // reports what the read truncated.
    const orders = (hasMore ? fetched.slice(0, limit) : fetched).filter(
      (o) => !isCheckoutHeldOrderState(o.orderState),
    );

    if (hasMore) {
      // A cap that nobody can see is how "All orders" quietly stopped meaning
      // all orders. Say it at the data layer too, not only in the UI.
      logger.warn("Restaurant orders truncated by limit", {
        tenantId,
        restaurantId: locationNumericId,
        filters,
        limit,
      });
    }

    logger.debug(`✅ Found ${orders.length} orders for restaurant`, { hasMore });
    return { orders, hasMore };
  } catch (error) {
    logger.error("Failed to get restaurant orders", error);
    throw error;
  }
};

/**
 * Gets all orders for a restaurant with optional filtering.
 *
 * Convenience wrapper that discards the truncation flag. Prefer
 * [getRestaurantOrdersPage] anywhere the result is presented as complete.
 */
const getLocationOrders = async (
  ...args: Parameters<typeof getLocationOrdersPage>
): Promise<Order[]> => (await getLocationOrdersPage(...args)).orders;

/**
 * Gets orders from a specific external source (Uber Eats, DoorDash, etc.)
 * @param tenantId The tenant numeric ID
 * @param locationNumericId The restaurant numeric ID
 * @param externalSource The external source platform
 * @param limit Optional limit on results
 * @returns Promise resolving to external orders
 */
const getExternalOrders = async (
  tenantId: number,
  locationNumericId: number,
  externalSource: "ubereats" | "doordash" | "grubhub" | "square" | "toast",
  limit: number = 100,
): Promise<Order[]> => {
  logger.debug(`Getting ${externalSource} orders`);

  try {
    return await getLocationOrders(
      tenantId,
      locationNumericId,
      {
        orderSource: "thirdParty",
        externalSource,
      },
      limit,
    );
  } catch (error) {
    logger.error(`Failed to get ${externalSource} orders`, error);
    throw error;
  }
};

/**
 * Gets all third-party orders (from any external platform)
 * @param tenantId The tenant numeric ID
 * @param locationNumericId The restaurant numeric ID
 * @param limit Optional limit on results
 * @returns Promise resolving to all third-party orders
 */
const getThirdPartyOrders = async (
  tenantId: number,
  locationNumericId: number,
  limit: number = 100,
): Promise<Order[]> => {
  logger.debug("Getting all third-party orders");

  try {
    return await getLocationOrders(
      tenantId,
      locationNumericId,
      {
        orderSource: "thirdParty",
      },
      limit,
    );
  } catch (error) {
    logger.error("Failed to get third-party orders", error);
    throw error;
  }
};

/**
 * Gets platform orders (POS, online ordering, kiosk)
 * @param tenantId The tenant numeric ID
 * @param locationNumericId The restaurant numeric ID
 * @param source The platform source (pos, online, kiosk, phone, api)
 * @param limit Optional limit on results
 * @returns Promise resolving to platform orders
 */
const getPlatformOrders = async (
  tenantId: number,
  locationNumericId: number,
  source: "pos" | "online" | "kiosk" | "phone" | "api",
  limit: number = 100,
): Promise<Order[]> => {
  logger.debug(`Getting ${source} orders`);

  try {
    return await getLocationOrders(
      tenantId,
      locationNumericId,
      {
        orderSource: source,
      },
      limit,
    );
  } catch (error) {
    logger.error(`Failed to get ${source} orders`, error);
    throw error;
  }
};

export const orderService = {
  getCustomerOrders,
  getOrderById,
  getOrderByGuid,
  getOrderByCartId,
  updateOrderStatus,
  getOrderAnalytics,
  // Management & filtering
  getRestaurantOrders: getLocationOrders,
  getRestaurantOrdersPage: getLocationOrdersPage,
  getExternalOrders,
  getThirdPartyOrders,
  getPlatformOrders,
  // Utility functions
};
